import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Col,
  Input,
  InputNumber,
  Modal,
  Radio,
  Row,
  Select,
  Space,
  Steps,
  Table,
  Tag,
  Typography,
  Upload,
  message,
} from 'antd';
import { DownloadOutlined, InboxOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { useMutation } from '@tanstack/react-query';
import { epcEstimationService } from '@/services/epcEstimation.service';
import {
  BOQ_IMPORT_PRESET_HINTS,
  BOQ_IMPORT_PRESET_LABELS,
  BOQ_IMPORT_TARGET_FIELDS,
  buildMappingPayload,
  parseEst3BoqWorkbook,
  readEst3BoqImportWorkbook,
  resolveBoqImportColumns,
  type BoqImportColumnKey,
  type BoqImportMappingPayload,
  type Est3BoqImportPreset,
  type Est3BoqImportPreviewRow,
  type ResolvedBoqImportColumns,
} from '@/utils/est3BoqImportParser';
import { formatMoney } from '@/utils/format';
import { EpcPdfBoqSourceViewer } from './EpcPdfBoqSourceViewer';

const { Text, Paragraph } = Typography;
const { Dragger } = Upload;

type SourceMode = 'excel' | 'pdf';
type Confidence = 'HIGH' | 'MEDIUM' | 'LOW' | 'REJECT';
type ConfidenceFilter = 'all' | 'review' | 'high' | 'errors';

type PdfImportRow = Est3BoqImportPreviewRow & {
  confidence?: Confidence;
  sourcePage?: number;
  sourcePageEnd?: number;
  rawText?: string;
  excluded?: boolean;
};

type PdfAnalyzeResult = {
  jobId?: string;
  status?: string;
  message?: string;
  documentId?: string;
  meta?: {
    pageCount?: number;
    boqPageCount?: number;
    isLikelyScanned?: boolean;
    fileName?: string;
    truncatedPages?: boolean;
    documentId?: string;
    async?: boolean;
    aiAssist?: {
      enabled?: boolean;
      applied?: boolean;
      suggestedExcludeCount?: number;
      notes?: string;
      error?: string;
    };
  };
  pages?: Array<{ pageNumber: number; pageClass: string; classReason?: string }>;
  rows?: PdfImportRow[];
  validCount?: number;
  errorCount?: number;
  warningCount?: number;
  reviewCount?: number;
  ready?: boolean;
};

function importNetworkErrorMessage(e: unknown, fallback: string): string {
  const err = e as {
    code?: string;
    message?: string;
    response?: { data?: { message?: string }; status?: number };
  };
  const apiMsg = err?.response?.data?.message;
  if (apiMsg) return apiMsg;
  const status = err?.response?.status;
  const msg = String(err?.message || '');
  const code = String(err?.code || '');
  if (status === 404) {
    return 'Import API returned 404 — usually the backend was restarting. Wait a few seconds and retry.';
  }
  if (
    code === 'ERR_NETWORK' ||
    /network error/i.test(msg) ||
    /connection refused/i.test(msg) ||
    (err?.response == null && /failed/i.test(msg))
  ) {
    return 'Cannot reach the API server. Wait for the backend on port 5002 to finish starting, then retry.';
  }
  if (code === 'ECONNABORTED' || /timeout/i.test(msg)) {
    return 'Request timed out. Retry once for large documents.';
  }
  return msg || fallback;
}

function confidenceTag(c?: Confidence) {
  if (c === 'HIGH') return <Tag color="green">HIGH</Tag>;
  if (c === 'MEDIUM') return <Tag color="orange">MEDIUM</Tag>;
  if (c === 'LOW') return <Tag color="volcano">LOW</Tag>;
  if (c === 'REJECT') return <Tag>REJECT</Tag>;
  return <Tag>—</Tag>;
}

export interface EpcEstimateBoqImportWizardProps {
  open: boolean;
  onClose: () => void;
  estimateId: string;
  revisionId: string;
  readOnly?: boolean;
  onImported: () => void;
}

type ServerPreview = {
  sheet?: string;
  sheets?: string[];
  detectedPreset?: string;
  validCount?: number;
  errorCount?: number;
  warningCount?: number;
  rowCount?: number;
  truncated?: boolean;
  ready?: boolean;
  rows?: Est3BoqImportPreviewRow[];
};

/**
 * EST3 Client BOQ import — KEEP_SEPARATE from Sales BOQ import wizards.
 * Excel: Upload → Map → Preview → Done
 * PDF Phase 1: Upload → Analyze/Review (confidence) → Done → same persist authority
 */
export const EpcEstimateBoqImportWizard: React.FC<EpcEstimateBoqImportWizardProps> = ({
  open,
  onClose,
  estimateId,
  revisionId,
  readOnly,
  onImported,
}) => {
  const [sourceMode, setSourceMode] = useState<SourceMode>('excel');
  const [step, setStep] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [uploadFile, setUploadFile] = useState<UploadFile | null>(null);
  const [sheets, setSheets] = useState<Array<{ name: string; aoa: unknown[][] }>>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [sheetName, setSheetName] = useState('');
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [headerRowIndex, setHeaderRowIndex] = useState(0);
  const [preset, setPreset] = useState<Est3BoqImportPreset>('auto');
  const [columnOverrides, setColumnOverrides] = useState<Partial<Record<BoqImportColumnKey, number>>>(
    {}
  );
  const [localSample, setLocalSample] = useState<Est3BoqImportPreviewRow[]>([]);
  const [serverPreview, setServerPreview] = useState<ServerPreview | null>(null);
  const [result, setResult] = useState<any>(null);

  const [pdfJobId, setPdfJobId] = useState<string | null>(null);
  const [pdfAnalyze, setPdfAnalyze] = useState<PdfAnalyzeResult | null>(null);
  const [pdfRows, setPdfRows] = useState<PdfImportRow[]>([]);
  const [confidenceFilter, setConfidenceFilter] = useState<ConfidenceFilter>('all');
  const [viewerPage, setViewerPage] = useState(1);
  const [pdfDocumentId, setPdfDocumentId] = useState<string | null>(null);
  const [importMode, setImportMode] = useState<'append' | 'replace'>('append');
  const [existingBoqCount, setExistingBoqCount] = useState(0);

  const resolvedCols: ResolvedBoqImportColumns | null = useMemo(() => {
    if (!headers.length) return null;
    return resolveBoqImportColumns(headers, {
      preset,
      columns: columnOverrides,
      deriveParentFromItemNumber: true,
      headerRowIndex,
    });
  }, [headers, preset, columnOverrides, headerRowIndex]);

  const mappingPayload: BoqImportMappingPayload | null = useMemo(() => {
    if (!resolvedCols) return null;
    return buildMappingPayload(
      preset,
      resolvedCols,
      headerRowIndex,
      true,
      sheetNames.length ? sheetNames : undefined
    );
  }, [resolvedCols, preset, headerRowIndex, sheetNames]);

  const reset = () => {
    setStep(0);
    setFile(null);
    setUploadFile(null);
    setSheets([]);
    setHeaders([]);
    setSheetName('');
    setSheetNames([]);
    setHeaderRowIndex(0);
    setPreset('auto');
    setColumnOverrides({});
    setLocalSample([]);
    setServerPreview(null);
    setResult(null);
    setPdfJobId(null);
    setPdfAnalyze(null);
    setPdfRows([]);
    setConfidenceFilter('all');
    setViewerPage(1);
    setPdfDocumentId(null);
    setImportMode('append');
    setExistingBoqCount(0);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const refreshLocalSample = (
    allSheets: Array<{ name: string; aoa: unknown[][] }>,
    hdrIdx: number,
    nextPreset: Est3BoqImportPreset,
    overrides: Partial<Record<BoqImportColumnKey, number>>,
    names: string[]
  ) => {
    const first = allSheets[0];
    const hdr = ((first?.aoa[hdrIdx] ?? []) as unknown[]).map((h) => String(h ?? ''));
    setHeaders(hdr);
    const rows = parseEst3BoqWorkbook(allSheets, {
      preset: nextPreset,
      columns: overrides,
      deriveParentFromItemNumber: true,
      headerRowIndex: hdrIdx,
      sheetNames: names.length ? names : undefined,
    });
    setLocalSample(rows.slice(0, 5));
  };

  const previewM = useMutation({
    mutationFn: () => {
      if (!file || !mappingPayload) throw new Error('No file or mapping');
      return epcEstimationService.previewClientBoqImport(
        estimateId,
        revisionId,
        file,
        mappingPayload as Record<string, unknown>
      );
    },
    onSuccess: (data) => {
      setServerPreview(data as ServerPreview);
      setStep(2);
    },
    onError: (e: any) => message.error(importNetworkErrorMessage(e, 'Preview failed')),
  });

  const importM = useMutation({
    mutationFn: () => {
      if (!file || !mappingPayload) throw new Error('No file or mapping');
      return epcEstimationService.importClientBoq(
        estimateId,
        revisionId,
        file,
        mappingPayload as Record<string, unknown>
      );
    },
    onSuccess: (data) => {
      setResult(data);
      setStep(3);
      message.success(`Imported ${data?.created ?? 0} BOQ line(s)`);
      onImported();
    },
    onError: (e: any) => message.error(importNetworkErrorMessage(e, 'Import failed')),
  });

  const analyzePdfM = useMutation({
    mutationFn: (pdfFile: File) => {
      return epcEstimationService.analyzeClientBoqPdf(estimateId, revisionId, pdfFile);
    },
    onSuccess: (data) => {
      const d = data as PdfAnalyzeResult;
      setPdfAnalyze(d);
      setPdfJobId(d.jobId || null);
      setPdfDocumentId(d.documentId || d.meta?.documentId || null);
      if (d.status === 'queued' || d.status === 'running') {
        message.info(d.message || 'Large PDF — analyzing in background…');
        setStep(1);
        return;
      }
      setPdfRows((d.rows || []).map((r) => ({ ...r })));
      const firstPage = d.rows?.find((r) => r.sourcePage)?.sourcePage || 1;
      setViewerPage(firstPage);
      void epcEstimationService.listClientBoq(estimateId, revisionId).then((lines: any) => {
        const arr = Array.isArray(lines) ? lines : lines?.items || [];
        setExistingBoqCount(arr.length);
        if (arr.length > 0) setImportMode('replace');
      });
      if (d.status === 'scanned_unsupported' || d.status === 'ocr_required') {
        message.warning(d.message || 'Scanned PDF — OCR required');
        setStep(1);
        return;
      }
      if (d.status === 'failed') {
        message.error(d.message || 'PDF analyze failed');
        setStep(1);
        return;
      }
      setStep(1);
    },
    onError: (e: any) => message.error(importNetworkErrorMessage(e, 'PDF analyze failed')),
  });

  const importPdfM = useMutation({
    mutationFn: () => {
      const rows = pdfRows.filter((r) => !r.excluded);
      return epcEstimationService.importClientBoqFromPdf(estimateId, {
        revisionId,
        jobId: pdfJobId || undefined,
        rows,
        fileName: pdfAnalyze?.meta?.fileName || file?.name,
        documentId: pdfDocumentId || undefined,
        importMode,
      });
    },
    onSuccess: (data) => {
      setResult(data);
      setStep(2);
      message.success(`Imported ${data?.created ?? 0} BOQ line(s) from PDF`);
      onImported();
    },
    onError: (e: any) => message.error(importNetworkErrorMessage(e, 'PDF import failed')),
  });

  const mutatePdfM = useMutation({
    mutationFn: (op: { type: 'merge' | 'split'; row: number }) => {
      if (!pdfJobId) throw new Error('No PDF job');
      return epcEstimationService.mutateClientBoqPdfRows(estimateId, {
        jobId: pdfJobId,
        ...op,
      });
    },
    onSuccess: (data) => {
      const d = data as PdfAnalyzeResult;
      if (d.rows) setPdfRows(d.rows.map((r) => ({ ...r })));
      message.success('Row structure updated');
    },
    onError: (e: any) => message.error(importNetworkErrorMessage(e, 'Merge/split failed')),
  });

  useEffect(() => {
    if (sourceMode !== 'pdf' || !pdfJobId) return;
    const st = pdfAnalyze?.status;
    if (st !== 'queued' && st !== 'running') return;
    let cancelled = false;
    const tick = async () => {
      try {
        const data = (await epcEstimationService.getClientBoqPdfAnalyzeJob(
          estimateId,
          pdfJobId
        )) as PdfAnalyzeResult;
        if (cancelled) return;
        setPdfAnalyze(data);
        setPdfDocumentId(data.documentId || data.meta?.documentId || null);
        if (data.status === 'completed') {
          setPdfRows((data.rows || []).map((r) => ({ ...r })));
          const firstPage = data.rows?.find((r) => r.sourcePage)?.sourcePage || 1;
          setViewerPage(firstPage);
          void epcEstimationService.listClientBoq(estimateId, revisionId).then((lines: any) => {
            const arr = Array.isArray(lines) ? lines : lines?.items || [];
            setExistingBoqCount(arr.length);
            if (arr.length > 0) setImportMode('replace');
          });
          message.success('PDF analysis complete — review rows');
        } else if (data.status === 'failed' || data.status === 'ocr_required') {
          message.warning(data.message || 'PDF analysis stopped');
        }
      } catch {
        /* keep polling until TTL */
      }
    };
    const id = window.setInterval(tick, 1500);
    void tick();
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [sourceMode, pdfJobId, pdfAnalyze?.status, estimateId, revisionId]);

  const previewRows = serverPreview?.rows || [];
  const validCount = serverPreview?.validCount ?? 0;
  const errorCount = serverPreview?.errorCount ?? 0;
  const warningCount = serverPreview?.warningCount ?? 0;

  const pdfImportableCount = pdfRows.filter(
    (r) => !r.excluded && (r.status === 'valid' || r.status === 'warning')
  ).length;
  const pdfReviewCount = pdfRows.filter(
    (r) =>
      !r.excluded &&
      (r.confidence === 'LOW' || r.confidence === 'MEDIUM' || r.status === 'warning' || r.status === 'error')
  ).length;

  const filteredPdfRows = useMemo(() => {
    return pdfRows.filter((r) => {
      if (confidenceFilter === 'all') return true;
      if (confidenceFilter === 'high') return !r.excluded && r.confidence === 'HIGH';
      if (confidenceFilter === 'errors') return !r.excluded && r.status === 'error';
      if (confidenceFilter === 'review') {
        return (
          !r.excluded &&
          (r.confidence === 'LOW' ||
            r.confidence === 'MEDIUM' ||
            r.status === 'warning' ||
            r.status === 'error')
        );
      }
      return true;
    });
  }, [pdfRows, confidenceFilter]);

  const headerOptions = useMemo(
    () => [
      { value: -1, label: '(unmapped)' },
      ...headers.map((h, i) => ({
        value: i,
        label: h?.trim() ? `Col ${i + 1}: ${h}` : `Col ${i + 1}: (empty)`,
      })),
    ],
    [headers]
  );

  const canProceedFromMap = !!resolvedCols && resolvedCols.description >= 0;

  const patchPdfRow = (rowNum: number, patch: Partial<PdfImportRow>) => {
    setPdfRows((prev) =>
      prev.map((r) => {
        if (r.row !== rowNum) return r;
        const next = { ...r, ...patch };
        if (next.excluded) {
          next.status = 'error';
          next.errorMessage = 'Excluded by reviewer';
          return next;
        }
        if (!String(next.description || '').trim()) {
          next.status = 'error';
          next.errorMessage = 'Description is required';
          return next;
        }
        if (!Number.isFinite(Number(next.quantity)) || Number(next.quantity) < 0) {
          next.status = 'error';
          next.errorMessage = 'Quantity must be non-negative';
          return next;
        }
        if (!Number.isFinite(Number(next.unitRate)) || Number(next.unitRate) < 0) {
          next.status = 'error';
          next.errorMessage = 'Unit Rate must be non-negative';
          return next;
        }
        next.status =
          next.confidence === 'LOW' || next.confidence === 'MEDIUM' ? 'warning' : 'valid';
        next.errorMessage = undefined;
        next.warningMessage =
          next.confidence === 'LOW' || next.confidence === 'MEDIUM'
            ? `${next.confidence} confidence — user reviewed`
            : undefined;
        if (next.confidence === 'LOW') next.confidence = 'MEDIUM';
        return next;
      })
    );
  };

  const excelSteps = [
    { title: 'Upload' },
    { title: 'Map' },
    { title: 'Preview' },
    { title: 'Done' },
  ];
  const pdfSteps = [
    { title: 'Upload' },
    { title: 'Review' },
    { title: 'Done' },
  ];

  return (
    <Modal
      title="Import Client BOQ (EST3)"
      open={open}
      onCancel={handleClose}
      width={sourceMode === 'pdf' ? 1280 : 920}
      destroyOnClose
      footer={
        <Space>
          <Button onClick={handleClose}>
            {(sourceMode === 'excel' && step === 3) || (sourceMode === 'pdf' && step === 2)
              ? 'Close'
              : 'Cancel'}
          </Button>
          {sourceMode === 'excel' && step === 1 && (
            <>
              <Button onClick={() => setStep(0)}>Back</Button>
              <Button
                type="primary"
                disabled={readOnly || !canProceedFromMap}
                loading={previewM.isPending}
                onClick={() => previewM.mutate()}
              >
                Preview on server
              </Button>
            </>
          )}
          {sourceMode === 'excel' && step === 2 && (
            <>
              <Button onClick={() => setStep(1)}>Back</Button>
              <Button
                type="primary"
                disabled={readOnly || validCount === 0}
                loading={importM.isPending}
                onClick={() => importM.mutate()}
              >
                Import {validCount} valid row(s)
              </Button>
            </>
          )}
          {sourceMode === 'pdf' &&
            step === 1 &&
            pdfAnalyze?.status !== 'scanned_unsupported' &&
            pdfAnalyze?.status !== 'ocr_required' &&
            pdfAnalyze?.status !== 'queued' &&
            pdfAnalyze?.status !== 'running' &&
            pdfAnalyze?.status !== 'failed' && (
            <>
              <Button onClick={() => setStep(0)}>Back</Button>
              <Button
                type="primary"
                disabled={readOnly || pdfImportableCount === 0}
                loading={importPdfM.isPending}
                onClick={() => importPdfM.mutate()}
              >
                Confirm import {pdfImportableCount} row(s)
                {importMode === 'replace' ? ' (replace)' : ''}
              </Button>
            </>
          )}
          {sourceMode === 'pdf' &&
            step === 1 &&
            (pdfAnalyze?.status === 'queued' ||
              pdfAnalyze?.status === 'running' ||
              pdfAnalyze?.status === 'failed' ||
              pdfAnalyze?.status === 'ocr_required' ||
              pdfAnalyze?.status === 'scanned_unsupported') && (
              <Button onClick={() => setStep(0)}>Back</Button>
            )}
        </Space>
      }
    >
      {readOnly ? (
        <Alert type="warning" showIcon message="Approved revision — import is read-only." />
      ) : (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Steps
            size="small"
            current={step}
            items={sourceMode === 'excel' ? excelSteps : pdfSteps}
          />

          {step === 0 && (
            <>
              <Radio.Group
                value={sourceMode}
                onChange={(e) => {
                  reset();
                  setSourceMode(e.target.value);
                }}
                optionType="button"
                buttonStyle="solid"
                options={[
                  { label: 'Excel', value: 'excel' },
                  { label: 'PDF (digital)', value: 'pdf' },
                ]}
              />
              {sourceMode === 'excel' ? (
                <>
                  <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                    Drop any Client BOQ Excel — map columns next. Hierarchy is built from Item Numbers
                    in the file. EST3 template is optional.
                  </Paragraph>
                  <Button
                    icon={<DownloadOutlined />}
                    onClick={async () => {
                      try {
                        const blob = await epcEstimationService.downloadClientBoqImportTemplate(
                          estimateId
                        );
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = 'est3-client-boq-import-template.xlsx';
                        a.click();
                        URL.revokeObjectURL(url);
                      } catch (e: any) {
                        message.error(e?.message || 'Template download failed');
                      }
                    }}
                  >
                    Download Excel template (optional)
                  </Button>
                  <Dragger
                    multiple={false}
                    maxCount={1}
                    accept=".xlsx,.xls"
                    fileList={uploadFile ? [uploadFile] : []}
                    beforeUpload={async (f) => {
                      const name = f.name.toLowerCase();
                      if (!name.endsWith('.xlsx') && !name.endsWith('.xls')) {
                        message.error('Only .xlsx or .xls files are allowed');
                        return false;
                      }
                      setFile(f);
                      setUploadFile({ uid: f.name, name: f.name, status: 'done' });
                      try {
                        const wb = await readEst3BoqImportWorkbook(f);
                        setSheets(wb.sheets);
                        setSheetName(wb.sheetName);
                        setSheetNames(wb.sheetNames);
                        setHeaderRowIndex(0);
                        setPreset('auto');
                        setColumnOverrides({});
                        const hdr = (wb.aoa[0] ?? []).map((h) => String(h ?? ''));
                        setHeaders(hdr);
                        refreshLocalSample(wb.sheets, 0, 'auto', {}, wb.sheetNames);
                        setStep(1);
                      } catch (e: any) {
                        message.error(importNetworkErrorMessage(e, 'Failed to read file'));
                      }
                      return false;
                    }}
                    onRemove={() => reset()}
                  >
                    <p className="ant-upload-drag-icon">
                      <InboxOutlined />
                    </p>
                    <p className="ant-upload-text">Drop Client BOQ Excel (.xlsx / .xls)</p>
                  </Dragger>
                </>
              ) : (
                <>
                  <Alert
                    type="info"
                    showIcon
                    message="PDF import — digital text + Phase 2 review workspace"
                    description="Source PDF is stored as estimate evidence. Review with side-by-side page viewer, merge/split, then confirm. Scanned PDFs need an OCR provider (PDF_BOQ_OCR_PROVIDER)."
                  />
                  <Dragger
                    multiple={false}
                    maxCount={1}
                    accept=".pdf,application/pdf"
                    fileList={uploadFile ? [uploadFile] : []}
                    beforeUpload={(f) => {
                      const name = f.name.toLowerCase();
                      if (!name.endsWith('.pdf')) {
                        message.error('Only .pdf files are allowed');
                        return false;
                      }
                      if (f.size > 25 * 1024 * 1024) {
                        message.error('PDF exceeds 25 MB Phase 1 limit');
                        return false;
                      }
                      setFile(f);
                      setUploadFile({ uid: f.name, name: f.name, status: 'done' });
                      analyzePdfM.mutate(f);
                      return false;
                    }}
                    onRemove={() => reset()}
                  >
                    <p className="ant-upload-drag-icon">
                      <InboxOutlined />
                    </p>
                    <p className="ant-upload-text">
                      {analyzePdfM.isPending
                        ? 'Analyzing PDF…'
                        : 'Drop client tender BOQ PDF (digital / text-based)'}
                    </p>
                  </Dragger>
                </>
              )}
            </>
          )}

          {sourceMode === 'excel' && step === 1 && (
            <>
              <Alert
                type="info"
                showIcon
                message={
                  <Space wrap>
                    <Text>
                      Sheets ({sheetNames.length || 1}):{' '}
                      <Text code>{sheetNames.join(' · ') || sheetName || '—'}</Text>
                    </Text>
                    <Text>
                      Detected:{' '}
                      <Tag>{BOQ_IMPORT_PRESET_LABELS[resolvedCols?.preset || 'custom']}</Tag>
                    </Text>
                  </Space>
                }
                description={
                  <>
                    {BOQ_IMPORT_PRESET_HINTS[preset]} All workbook sheets are imported by default
                    (column map from the first sheet). Parents are tracked from Item Numbers in Excel.
                  </>
                }
              />
              <Space wrap>
                <Text>Format</Text>
                <Select
                  style={{ minWidth: 220 }}
                  value={preset}
                  options={(Object.keys(BOQ_IMPORT_PRESET_LABELS) as Est3BoqImportPreset[]).map(
                    (k) => ({ value: k, label: BOQ_IMPORT_PRESET_LABELS[k] })
                  )}
                  onChange={(v: Est3BoqImportPreset) => {
                    setPreset(v);
                    setColumnOverrides({});
                    refreshLocalSample(sheets, headerRowIndex, v, {}, sheetNames);
                  }}
                />
                <Text>Header row</Text>
                <InputNumber
                  min={0}
                  max={5}
                  value={headerRowIndex}
                  onChange={(v) => {
                    const idx = typeof v === 'number' ? v : 0;
                    setHeaderRowIndex(idx);
                    setColumnOverrides({});
                    refreshLocalSample(sheets, idx, preset, {}, sheetNames);
                  }}
                />
              </Space>
              <Table
                size="small"
                pagination={false}
                rowKey="key"
                dataSource={BOQ_IMPORT_TARGET_FIELDS.map((f) => ({
                  key: f.key,
                  label: f.label,
                  required: f.required,
                  value: resolvedCols?.[f.key] ?? -1,
                }))}
                columns={[
                  {
                    title: 'Target field',
                    dataIndex: 'label',
                    render: (
                      label: string,
                      r: { key: BoqImportColumnKey; label: string; required?: boolean; value: number }
                    ) => (
                      <span>
                        {label}
                        {r.required ? <Text type="danger"> *</Text> : null}
                      </span>
                    ),
                  },
                  {
                    title: 'Spreadsheet column',
                    render: (
                      _: unknown,
                      r: { key: BoqImportColumnKey; label: string; required?: boolean; value: number }
                    ) => (
                      <Select
                        style={{ width: '100%' }}
                        options={headerOptions}
                        value={r.value}
                        onChange={(colIdx: number) => {
                          const next = { ...columnOverrides, [r.key]: colIdx };
                          setColumnOverrides(next);
                          refreshLocalSample(sheets, headerRowIndex, preset, next, sheetNames);
                        }}
                      />
                    ),
                  },
                ]}
              />
              {!canProceedFromMap && (
                <Alert type="error" showIcon message="Map Description before previewing." />
              )}
              <Text type="secondary">Sample (first rows, local)</Text>
              <Table
                size="small"
                rowKey="key"
                dataSource={localSample}
                pagination={false}
                scroll={{ x: true }}
                columns={[
                  { title: 'Row', dataIndex: 'row', width: 60 },
                  { title: 'Item', dataIndex: 'itemNumber', width: 90 },
                  { title: 'Description', dataIndex: 'description', ellipsis: true },
                  { title: 'Parent', dataIndex: 'parentItemNumber', width: 90 },
                  { title: 'Qty', dataIndex: 'quantity', width: 80 },
                  { title: 'UOM', dataIndex: 'uom', width: 70 },
                  {
                    title: 'Rate',
                    dataIndex: 'unitRate',
                    width: 100,
                    render: (v) => formatMoney(v, { display: 'plain', maximumFractionDigits: 2 }),
                  },
                  {
                    title: 'Status',
                    width: 180,
                    render: (_: unknown, r: Est3BoqImportPreviewRow) =>
                      r.status === 'valid' ? (
                        <Tag color="green">valid</Tag>
                      ) : r.status === 'warning' ? (
                        <Tag color="orange">{r.warningMessage || 'warning'}</Tag>
                      ) : (
                        <Tag color="red">{r.errorMessage}</Tag>
                      ),
                  },
                ]}
              />
            </>
          )}

          {sourceMode === 'excel' && step === 2 && (
            <>
              <Space wrap>
                <Tag color="green">{validCount} valid</Tag>
                <Tag color={warningCount ? 'orange' : 'default'}>{warningCount} warnings</Tag>
                <Tag color={errorCount ? 'red' : 'default'}>{errorCount} errors</Tag>
                {serverPreview?.truncated ? <Tag>Preview truncated</Tag> : null}
                <Text type="secondary">
                  {(serverPreview?.sheets || []).length
                    ? `${serverPreview?.sheets?.length} sheet(s)`
                    : `Sheet ${serverPreview?.sheet || sheetName}`}{' '}
                  · {serverPreview?.rowCount ?? 0} rows
                </Text>
              </Space>
              <Table
                size="small"
                rowKey={(r) => String(r.row)}
                dataSource={previewRows}
                pagination={{ pageSize: 20 }}
                scroll={{ x: true }}
                columns={[
                  { title: 'Sheet', dataIndex: 'sheet', width: 140, ellipsis: true },
                  { title: 'Row', dataIndex: 'row', width: 60 },
                  { title: 'Item', dataIndex: 'itemNumber', width: 90 },
                  { title: 'Description', dataIndex: 'description', ellipsis: true },
                  { title: 'Parent', dataIndex: 'parentItemNumber', width: 90 },
                  { title: 'Qty', dataIndex: 'quantity', width: 80 },
                  { title: 'UOM', dataIndex: 'uom', width: 70 },
                  {
                    title: 'Rate',
                    dataIndex: 'unitRate',
                    width: 100,
                    render: (v) => formatMoney(v, { display: 'plain', maximumFractionDigits: 2 }),
                  },
                  {
                    title: 'Status',
                    width: 200,
                    render: (_: unknown, r: Est3BoqImportPreviewRow) =>
                      r.status === 'valid' ? (
                        <Tag color="green">valid</Tag>
                      ) : r.status === 'warning' ? (
                        <Tag color="orange">{r.warningMessage || 'warning'}</Tag>
                      ) : (
                        <Tag color="red">{r.errorMessage}</Tag>
                      ),
                  },
                ]}
              />
            </>
          )}

          {sourceMode === 'pdf' && step === 1 && (
            <>
              {pdfAnalyze?.status === 'queued' || pdfAnalyze?.status === 'running' ? (
                <Alert
                  type="info"
                  showIcon
                  message={pdfAnalyze.status === 'queued' ? 'Queued' : 'Analyzing PDF…'}
                  description={pdfAnalyze.message || 'Polling job status. You can wait here.'}
                />
              ) : pdfAnalyze?.status === 'failed' ? (
                <Alert type="error" showIcon message="Analyze failed" description={pdfAnalyze.message} />
              ) : pdfAnalyze?.status === 'scanned_unsupported' ||
                pdfAnalyze?.status === 'ocr_required' ? (
                <Alert
                  type="error"
                  showIcon
                  message="Scanned PDF — OCR required"
                  description={
                    <>
                      {pdfAnalyze.message}
                      {pdfDocumentId ? (
                        <>
                          {' '}
                          Source PDF was stored as document evidence (id {pdfDocumentId}).
                        </>
                      ) : null}
                    </>
                  }
                />
              ) : (
                <Row gutter={16}>
                  <Col xs={24} lg={9}>
                    <EpcPdfBoqSourceViewer
                      file={file}
                      pageNumber={viewerPage}
                      onPageChange={setViewerPage}
                      height={520}
                    />
                    {pdfDocumentId ? (
                      <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                        Stored document: {pdfDocumentId}
                      </Text>
                    ) : null}
                  </Col>
                  <Col xs={24} lg={15}>
                    <Alert
                      type="warning"
                      showIcon
                      message="Review interpreted BOQ before commit"
                      description={
                        <>
                          {pdfAnalyze?.meta?.pageCount ?? 0} page(s) ·{' '}
                          {pdfAnalyze?.meta?.boqPageCount ?? 0} BOQ-class · {pdfReviewCount} need
                          attention · {pdfImportableCount} ready. Click a page cell to sync the
                          viewer.
                          {pdfAnalyze?.message ? ` ${pdfAnalyze.message}` : ''}
                          {pdfAnalyze?.meta?.aiAssist?.applied
                            ? ` AI suggested exclude: ${pdfAnalyze.meta.aiAssist.suggestedExcludeCount || 0} row(s).`
                            : ''}
                        </>
                      }
                    />
                    {pdfDocumentId ? (
                      <Button
                        size="small"
                        style={{ marginTop: 8 }}
                        onClick={async () => {
                          try {
                            const { documentService } = await import(
                              '@/services/document.service'
                            );
                            await documentService.openDocumentInNewTab(pdfDocumentId);
                          } catch (e: any) {
                            message.error(e?.message || 'Could not open source PDF');
                          }
                        }}
                      >
                        Open stored source PDF
                      </Button>
                    ) : null}
                    {existingBoqCount > 0 ? (
                      <Alert
                        style={{ marginTop: 8 }}
                        type="info"
                        showIcon
                        message={`Revision already has ${existingBoqCount} Client BOQ line(s)`}
                        description={
                          <Space direction="vertical">
                            <Text>
                              Use <Text strong>Replace</Text> to soft-delete existing lines then
                              import (recommended), or Append to add duplicates.
                            </Text>
                            <Radio.Group
                              value={importMode}
                              onChange={(e) => setImportMode(e.target.value)}
                              options={[
                                { label: 'Replace existing', value: 'replace' },
                                { label: 'Append', value: 'append' },
                              ]}
                            />
                          </Space>
                        }
                      />
                    ) : null}
                    <Space wrap style={{ marginTop: 8, marginBottom: 8 }}>
                      <Text>Filter</Text>
                      <Select
                        style={{ width: 180 }}
                        value={confidenceFilter}
                        onChange={(v: ConfidenceFilter) => setConfidenceFilter(v)}
                        options={[
                          { value: 'all', label: 'All rows' },
                          { value: 'review', label: 'Needs review' },
                          { value: 'high', label: 'HIGH only' },
                          { value: 'errors', label: 'Errors / blocked' },
                        ]}
                      />
                      <Tag color="green">{pdfImportableCount} importable</Tag>
                      <Tag color="orange">{pdfReviewCount} review</Tag>
                      <Button
                        size="small"
                        onClick={() =>
                          setPdfRows((prev) =>
                            prev.map((r) =>
                              r.status === 'error' && !r.excluded
                                ? {
                                    ...r,
                                    excluded: true,
                                    status: 'error',
                                    errorMessage: 'Excluded by reviewer',
                                  }
                                : r
                            )
                          )
                        }
                      >
                        Exclude all errors
                      </Button>
                    </Space>
                    <Table
                      size="small"
                      rowKey={(r) => String(r.row)}
                      dataSource={filteredPdfRows}
                      pagination={{ pageSize: 50, pageSizeOptions: [25, 50, 100, 200], showSizeChanger: true }}
                      scroll={{ x: 1100, y: 420 }}
                      onRow={(r) => ({
                        onClick: () => {
                          if (r.sourcePage) setViewerPage(r.sourcePage);
                        },
                      })}
                      columns={[
                        {
                          title: 'Conf.',
                          width: 80,
                          render: (_: unknown, r: PdfImportRow) => confidenceTag(r.confidence),
                        },
                        {
                          title: 'Page',
                          width: 64,
                          render: (_: unknown, r: PdfImportRow) => (
                            <Button
                              type="link"
                              size="small"
                              style={{ padding: 0 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (r.sourcePage) setViewerPage(r.sourcePage);
                              }}
                            >
                              {r.sourcePageEnd && r.sourcePageEnd !== r.sourcePage
                                ? `${r.sourcePage}-${r.sourcePageEnd}`
                                : String(r.sourcePage ?? '—')}
                            </Button>
                          ),
                        },
                        {
                          title: 'Item',
                          width: 90,
                          render: (_: unknown, r: PdfImportRow) => (
                            <Input
                              size="small"
                              value={r.itemNumber || ''}
                              disabled={!!r.excluded}
                              onChange={(e) => patchPdfRow(r.row, { itemNumber: e.target.value })}
                            />
                          ),
                        },
                        {
                          title: 'Description',
                          render: (_: unknown, r: PdfImportRow) => (
                            <Input.TextArea
                              size="small"
                              autoSize={{ minRows: 1, maxRows: 3 }}
                              value={r.description || ''}
                              disabled={!!r.excluded}
                              onChange={(e) => patchPdfRow(r.row, { description: e.target.value })}
                            />
                          ),
                        },
                        {
                          title: 'Qty',
                          width: 80,
                          render: (_: unknown, r: PdfImportRow) => (
                            <InputNumber
                              size="small"
                              style={{ width: '100%' }}
                              min={0}
                              value={r.quantity}
                              disabled={!!r.excluded}
                              onChange={(v) =>
                                patchPdfRow(r.row, { quantity: typeof v === 'number' ? v : 0 })
                              }
                            />
                          ),
                        },
                        {
                          title: 'UOM',
                          width: 70,
                          render: (_: unknown, r: PdfImportRow) => (
                            <Input
                              size="small"
                              value={r.uom || ''}
                              disabled={!!r.excluded}
                              onChange={(e) => patchPdfRow(r.row, { uom: e.target.value })}
                            />
                          ),
                        },
                        {
                          title: 'Rate',
                          width: 90,
                          render: (_: unknown, r: PdfImportRow) => (
                            <InputNumber
                              size="small"
                              style={{ width: '100%' }}
                              min={0}
                              value={r.unitRate}
                              disabled={!!r.excluded}
                              onChange={(v) =>
                                patchPdfRow(r.row, { unitRate: typeof v === 'number' ? v : 0 })
                              }
                            />
                          ),
                        },
                        {
                          title: 'Status',
                          width: 140,
                          render: (_: unknown, r: PdfImportRow) =>
                            r.excluded ? (
                              <Tag>excluded</Tag>
                            ) : r.status === 'valid' ? (
                              <Tag color="green">valid</Tag>
                            ) : r.status === 'warning' ? (
                              <Tag color="orange">{r.warningMessage || 'warning'}</Tag>
                            ) : (
                              <Tag color="red">{r.errorMessage}</Tag>
                            ),
                        },
                        {
                          title: 'Fix',
                          width: 160,
                          render: (_: unknown, r: PdfImportRow) => (
                            <Space size={0} wrap>
                              <Button
                                size="small"
                                type="link"
                                disabled={!!r.excluded}
                                loading={mutatePdfM.isPending}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  mutatePdfM.mutate({ type: 'merge', row: r.row });
                                }}
                              >
                                Merge↓
                              </Button>
                              <Button
                                size="small"
                                type="link"
                                disabled={!!r.excluded}
                                loading={mutatePdfM.isPending}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  mutatePdfM.mutate({ type: 'split', row: r.row });
                                }}
                              >
                                Split
                              </Button>
                              <Button
                                size="small"
                                type={r.excluded ? 'link' : 'text'}
                                danger={!r.excluded}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  patchPdfRow(r.row, { excluded: !r.excluded });
                                }}
                              >
                                {r.excluded ? 'Include' : 'Exclude'}
                              </Button>
                            </Space>
                          ),
                        },
                      ]}
                    />
                  </Col>
                </Row>
              )}
            </>
          )}

          {((sourceMode === 'excel' && step === 3) || (sourceMode === 'pdf' && step === 2)) && (
            <Alert
              type="success"
              showIcon
              message={`Created ${result?.created ?? 0} line(s); linked ${result?.linkedParents ?? 0} parent(s)`}
              description={
                result?.errors?.length
                  ? `${result.errors.length} row warning(s) — see server response.`
                  : `Batch ${result?.batchId || '—'}. Provenance stored in meta_json (source=${sourceMode}${
                      pdfDocumentId ? `, documentId=${pdfDocumentId}` : ''
                    }).`
              }
            />
          )}
        </Space>
      )}
    </Modal>
  );
};
